/**
 * `Listing` is abstract class used to create any listings (product, category, error, static, .. )
 * Listing can be pretty much anything
 *
 * # Listing type
 *
 * Each listing needs `type` string property set. This is to distinguish what kind of listing it is.
 * Example: product, category, ..
 *
 * This variable is used in many places of code and is absolutely required.
 *
 * Note that type determines from what mongo table data is loaded.
 *
 * # Routable listings
 *
 * If listing is routable it requires private property _routable = true.
 * Everytime instance of this listing is loaded its path is registered in resource
 *
 *
 * # Listing id
 *
 * Id can be either string or number. It's main way to load and identify listings.
 *
 *
 * # Singleton listing
 *
 * When listing has as only one instance (like customer) then it does not require id.
 * Such listing is singleton and we can create it by registration in resource with singleton : true option.
 * Any other listing will require id to be identified.
 *
 *
 * ## Creating listing class
 *
 * This is product class example.
 * `Product` class is created simply by extension of `Listing`.
 * We declare required `type` and for this kind of listing we add _routable flag also.
 * After declaration we register our listing in resource (other way it can be used, but not with resource)
 * Note that Resource is static class.
 *
 *
 * export class Product<T> extends Listing<T> {
 *      type = 'product';
 *      protected _routable = true;
 * }
 *
 * Resource.register({
 *      typeName: 'product',
 *      instanceClass :  Product
 * });
 *
 */
/**
 * Loads subscription data on page init
 */
import { Type } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { HashStorageService } from '@df/browser/storage/hash-storage.service';
import { StateStorageService } from '@df/browser/storage/state-storage.service';
import { Filter, IFilterConfig, TFilterType } from '@df/catalog-ui/listing/filter/filter';
import { IListingSizeOption, ListingSize } from '@df/catalog-ui/listing/size/listing-size';
import { ListingSizeFactory } from '@df/catalog-ui/listing/size/listing-size.factory';
import { IListingSortOption, ListingSort } from '@df/catalog-ui/listing/sort/listing-sort';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { IModelData, Model } from '@df/catalog/model';
import { App } from '@df/core/app';
import { TNumberPerSize } from '@df/core/df-core.interfaces';
import { Meta } from '@df/core/meta/meta';
import { Client } from '@df/core/service/client';
import { Events } from '@df/core/service/event';
import { Logger } from '@df/core/service/logger';
import { NgInject } from '@df/ng/ng-inject';
import { TimeoutService } from '@df/ng/timeout.service';
import cloneDeep from 'lodash-es/cloneDeep';
import defaults from 'lodash-es/defaults';
import isEqual from 'lodash-es/isEqual';
import isFunction from 'lodash-es/isFunction';
import omit from 'lodash-es/omit';
import sortBy from 'lodash-es/sortBy';
import take from 'lodash-es/take';
import { Subject, Subscription } from 'rxjs';

/**
 * Listing definition config interface
 */
export interface IListingConfig<T = Model> {
    /**
     * Models type
     */
    type: string;
    /**
     * Page step for inf scroll
     */
    infScrollStep?: number | (() => number) | number[];

    /**
     * Infor scroll initial qty
     */
    infScrollInit?: number | number[];

    /**
     * Number of steps visible initially
     */
    initialStepsVisible?: number;
    /**
     * It limit should be set by inf scroll step
     */
    noLimitReset?: boolean;
    /**
     * Storage identifier
     */
    storage?: boolean;
    // source model - for category page its category model
    // its may be used by some 3rd party solutions to have access to category
    source?: any;
    // when true models passed from input will be used directly without conversion
    directModels?: boolean;
    // used to pass any custom things like pioAllowed flag
    custom?: any;
    /**
     * With this option undefined property matches selection in filters
     */
    undefinedMatches?: boolean;
}

export interface IListingAsyncConfig {
    /**
     * Loading filter
     */
    toLoadFilter: () => any;
}

export type TAttrOptionSelection = { [attrCode: string]: number[] };

/**
 * Filter definition from
 */
export interface IFilterDefinition {
    /**
     * attribute or category
     * @type {string}
     */
    type: TFilterType;
    /**
     * For attribute filter its attribute code
     * For category filter its either childre or siblings
     * @type {string}
     */
    source?: string;
    /**
     * Open by default
     * @type {number}
     */
    is_open?: number;
    /**
     * Attribute position
     * Filter position (will be index in filters if not defined)
     * @type {number}
     */
    position?: number;
    /**
     * ?
     * @type {boolean}
     */
    // results?: boolean;
}

export const LISTING_UPDATE_EVENT = 'listing.update';
export const LISTING_UPDATE_TOTALS_EVENT = 'listing.update.totals';
export const LISTING_UPDATE_VISIBLE_MODELS = 'listing.update.visiblemodels';
export const LISTING_FILTER = 'listing.filter';
export const LISTING_SET_FILTERS = 'listing.setFilters';
export const LISTING_GET_FILTERS = 'listing.getFilters';

export interface IMethodWrapEvent<R, T extends Model = Model, D = undefined> {
    cb: () => R;
    data?: D;
    listing: Listing<T>;
}

export interface IFilterState {
    [id: string]: string | string[] | number[];
}
/**
 * Default listing configuration
 */
const DEFAULT_LISTING_CONFIG: IListingConfig = {
    // type of models
    type: 'product',
    // scroll step
    infScrollStep: 0,
    initialStepsVisible: 3,
    custom: {}
};
interface IListingFilterEntry<T extends Model> {
    instance: Type<Filter<T>>;
    options: IListingFilter;
}

const listingFilters: {
    [filterType: string]: IListingFilterEntry<any>;
} = {};

/**
 * Listing filter decorator options
 */

export interface IListingFilter {
    filterType: string;
}

export interface IListingFilterEvent<T extends Model = Model> {
    listing: Listing<T>;
    selection: any;
    overrides: IOverride<T>[] | undefined;
    models: T[];
}

export interface IOverride<T extends Model = Model> {
    filter: Filter<T>;
    selection: any;
}

/**
 * Listing filter decorator
 */
export function ListingFilter<T extends Model = Model>(component: IListingFilter) {
    return (constructor: Type<Filter<T>>) => {
        const entry: IListingFilterEntry<T> = {
            instance: constructor,
            options: component
        };
        listingFilters[component.filterType] = entry;
        constructor.prototype.filterType = component.filterType;
    };
}

export interface IListingSetup<T extends Model<IModelData, string | number> = Model> {
    filtersDefinitions?: (string | IFilterDefinition)[];
    sortOptions?: IListingSortOption[];
    sizeOptions?: IListingSizeOption[];
    filters?: Filter<T>[];
    sort?: ListingSort<T>;
    size?: ListingSize;
    defaultSize?: TNumberPerSize;
}

export class Listing<T extends Model<IModelData, string | number> = Model> {
    /**
     * Debounced version of apply filters
     */
    apply: () => void;

    /**
     * Bare models array without any visual manipulations on their list or order
     */
    protected modelsBare: T[] = [];

    /**
     * Models after filters applied
     */
    protected modelsFiltered: T[] = [];

    /**
     * Final models list after filters and sort applied
     */
    protected _models: T[] = [];
    /**
     * Final models list after filters and sort applied + limited
     */
    models: T[] = [];

    /**
     * Models + next step (that is intended not to be visible)
     */
    modelsWithOverflow: T[] = [];

    /**
     * Initial limit (before we increment page or we scroll)
     */
    protected initialLimit = 0;

    /**
     * Current limit
     * NOTE it may not be set, we take initialLimit then
     */
    protected limit?: number;

    /**
     * Subject for listing updates observation
     */
    protected subject = new Subject<T[]>();

    /**
     * Filters colletion map
     * id is filter id
     */
    filtersMap: { [id: string]: Filter<T> } = {};

    /**
     * Sort model
     */
    sort: ListingSort<T> | undefined;

    /**
     * Size model
     */
    size: ListingSize | undefined;

    /**
     * Listing config
     */
    config: IListingConfig;

    clearSubject = new Subject<void>();

    /**
     * Final models change subject
     */
    finalModelsChange = new Subject<T[]>();
    /**
     * Total number of items
     */
    total = 0;
    /**
     * Number of visible items
     */
    visibleQty = 0;

    protected subscriptions = new Subscription();

    protected set filtersState(filtersState: IFilterState) {
        const transport: IMethodWrapEvent<void, T, IFilterState> = {
            cb: () => this.hashStorageService.set('filters', transport.data),
            data: filtersState,
            listing: this
        };
        this.events.broadcast(LISTING_SET_FILTERS, transport);
        transport.cb();
    }

    protected get filtersState(): IFilterState {
        const transport: IMethodWrapEvent<IFilterState, T> = {
            cb: () => this.hashStorageService.get('filters'),
            listing: this
        };
        this.events.broadcast(LISTING_GET_FILTERS, transport);
        return transport.cb();
    }

    /**
     * Hash storage for sort
     */
    protected set sortState(sortState: string | undefined) {
        this.hashStorageService.set('sort', sortState);
    }
    protected get sortState(): string | undefined {
        return this.hashStorageService.get('sort');
    }

    @NgInject(HashStorageService)
    private hashStorageService!: HashStorageService;
    /**
     * Meta service
     */
    @NgInject(Meta)
    private meta!: Meta;

    /**
     * Meta service
     */
    @NgInject(Logger)
    protected logger!: Logger;
    /**
     * Client service
     */
    @NgInject(Client)
    protected client!: Client;
    /**
     * Events service
     */
    @NgInject(Events)
    protected events!: Events;
    /**
     * Events service
     */
    @NgInject(StateStorageService)
    protected stateStorageService!: StateStorageService;
    /**
     * App service
     */
    @NgInject(App)
    protected app!: App;

    @NgInject(Router)
    protected router!: Router;

    @NgInject(TimeoutService)
    private timeoutService!: TimeoutService;

    @NgInject(ListingSizeFactory)
    private listingSizeFactory!: ListingSizeFactory;

    /**
     * State for size
     */
    sizeState?: number;

    get limitState(): number | undefined {
        return this.stateStorageService.get('listingLimit');
    }
    set limitState(limitState: number | undefined) {
        this.stateStorageService.set('listingLimit', limitState);
    }

    /**
     * Listing ready flag
     */
    ready = false;

    constructor(config: Partial<IListingConfig>, models: T[] | number[], setup: IListingSetup<T> = {}) {
        this.apply = this.timeoutService.debounce(() => this.applyFilters(), 20);
        // set config
        this.config = defaults(config, DEFAULT_LISTING_CONFIG);
        this.setSourceModels(models);

        if (setup.filters) {
            setup.filters.forEach(filter => {
                this.addFilter(filter);
            });
        } else if (setup.filtersDefinitions) {
            this.setAttributeFilters(setup.filtersDefinitions);
        }

        if (setup.sort) {
            this.setSort(setup.sort);
        } else if (setup.sortOptions) {
            this.setSortOptions(setup.sortOptions);
        }

        if (setup.size) {
            this.setListingSize(setup.size);
        } else if (setup.sizeOptions) {
            this.setSizeOptions(setup.sizeOptions, setup.defaultSize);
        }

        if (this.config.storage) {
            this.timeoutService.setTimeout(() => {
                const limit = this.limitState;
                if (limit) {
                    this.setLimit(limit, true);
                }
            }, 20);

            // router event fires after listing is printed
            const routerSubscrption = this.meta.routerEvents.subscribe(event => {
                if (event instanceof NavigationEnd) {
                    if (this?.limitState) {
                        this.setLimit(this.limitState, true);
                    }
                    routerSubscrption.unsubscribe();
                }
            });

            this.subscriptions.add(
                this.router.events.subscribe(event => {
                    if (event instanceof NavigationEnd) {
                        this.setFiltersState();
                        this.setSortState();
                        this.apply();
                    }
                })
            );
        }

        this.setModelsFiltered();
        this.onInit();
        this.applyFilters();
    }

    ////////////////////////////////////////////
    //
    //  EVENTS
    //
    ////////////////////////////////////////////

    /**
     * O init hook
     */
    onInit() {}

    /**
     * Registers callback triggered when sort option changes
     */
    subscribe(...args): Subscription {
        return this.subject.subscribe(...args);
    }

    /**
     * Registers callback triggered when sort option changes
     */
    next() {
        // do not call events during initiation process
        if (!this.ready) {
            return;
        }
        this.subject.next(this.models);
        if (this.config.storage) {
            if (this.size) {
                this.sizeState = this.size.getState();
            }
        }
        this.events.broadcast(LISTING_UPDATE_EVENT, this);
    }

    private addSortToStorage(): void {
        this.sortState = this.sort?.getState();
    }

    protected addFiltersToStorage(): void {
        this.filtersState = this.prepareFiltersState();
    }

    prepareFiltersState(): IFilterState {
        const filtersState: any = {};
        this.filters.forEach(filter => {
            const val = filter.getState();
            if (val) {
                filtersState[filter.id] = val;
            }
        });
        return filtersState;
    }

    clearSubscriptions(): void {
        this.clearSubject.next();
        this.size?.clearSubscriptions();
        this.subscriptions.unsubscribe();
    }

    ////////////////////////////////////////////
    //
    //  CONFIG
    //
    ////////////////////////////////////////////

    /**
     * Models type getter
     */
    get type() {
        return this.config.type;
    }

    /**
     * Sets infinite scroll step size
     */
    setInfScrollStep(value: number | undefined | (() => number)) {
        this.config.infScrollStep = value;
        this.updateLimits();
    }

    /**
     * Sort options setter
     * @param {IListingSortOption[]} sortOptions
     */
    setSortOptions(options: IListingSortOption[]) {
        // we need to create deep copy here otherwise same object will be passed between categories (with optios marked as active)
        const sortOptions = cloneDeep(options);

        if (!this.sort) {
            this.setSort(new ListingSort<T>(this, sortOptions));
        } else {
            this.sort.setSortOptions(sortOptions);
        }
    }

    setSort(sort: ListingSort<T>): void {
        this.sort = sort;
        this.subscriptions.add(
            this.sort.subscribe(() => {
                if (this.config.storage) {
                    this.addSortToStorage();
                }
                this.sortModels();
            })
        );
        if (this.config.storage) {
            const sortState = this.sortState;
            if (sortState) {
                this.sort.setState(sortState);
            }
        }
    }

    setSortState(): void {
        if (this.sort) {
            this.sort.setState(this.sortState || '');
        }
    }

    /**
     * Sort options setter
     * @param {IListingSizeOption[]} sizeOptions
     */
    setSizeOptions(options: IListingSizeOption[], defaltSize?: [number, number, number, number]) {
        // we need to create deep copy here otherwise same object will be passed between categories (with optios marked as active)
        const sizeOptions = cloneDeep(options);

        if (!this.size) {
            this.setListingSize(this.listingSizeFactory.create(sizeOptions, defaltSize));
        } else {
            this.size.setSizeOptions(sizeOptions);
            if (defaltSize) {
                this.size.setDefaultSize(defaltSize);
            }
        }
    }

    setListingSize(size: ListingSize): void {
        this.size = size;
        this.subscriptions.add(this.size.subscribe((sizeOption: IListingSizeOption) => this.setSize(sizeOption.size)));
        if (this.config.storage) {
            const sizeState = this.sizeState;
            if (sizeState) {
                this.size.setState(sizeState);
            }
        }
    }

    /**
     * Filters setter
     * @param {string[]} filters attributes
     */
    setAttributeFilters(filterDefs: (string | IFilterDefinition)[]) {
        // normalize
        const filterDefNormalized = sortBy(
            (filterDefs as (string | IFilterDefinition)[]).map((filterDef, position) => {
                if (typeof filterDef === 'string') {
                    return {
                        type: 'attribute',
                        source: filterDef,
                        position
                    };
                }
                if (filterDef.position === undefined) {
                    filterDef.position = position;
                }

                return filterDef;
            }),
            'position'
        );

        filterDefNormalized.forEach((filterDef: IFilterDefinition): Filter<T> | void => this.createFilter(filterDef));
    }

    protected createFilter(filterDef: IFilterDefinition): void {
        const filterType: string | undefined = this.getFilterType(filterDef);

        if (filterType) {
            const filterModel = this.listingFilters[filterType];

            if (filterModel) {
                const filterConfig: IFilterConfig<string> = {
                    id: filterDef.source || filterType,
                    position: <number>filterDef.position,
                    isOpen: !!filterDef.is_open,
                    ...omit(filterDef, 'is_open', 'position', 'source', 'id', 'type'),
                    type: filterType
                };

                this.addFilter(<Filter<T>>new filterModel.instance(filterConfig, this));
            } else {
                this.logger.error('Unsupported filter type: ', filterType);
            }
        }

        return;
    }

    protected getFilterType(filterDef: IFilterDefinition): string | undefined {
        if (filterDef.type === 'attribute') {
            const attribute = filterDef.source && this.app.resources.attribute.getExisting(filterDef.source);

            if (attribute) {
                return attribute.data!.range ? 'attribute-range' : 'attribute-option';
            } else {
                this.logger.error('Attribute does not existg: ', filterDef.source);
            }
        }

        return filterDef.type;
    }

    protected get listingFilters() {
        return listingFilters;
    }

    ////////////////////////////////////////////
    //
    //  MODELS
    //
    ////////////////////////////////////////////

    getFinalModels() {
        return this._models;
    }

    setFinalModels(models: T[]) {
        // only apply if changed
        const isChanged =
            this._models &&
            !isEqual(
                models.map(i => i?.id),
                this._models.map(i => i?.id)
            );
        this._models = models;

        // only if change actually happenes
        if (isChanged) {
            this.finalModelsChange.next(models);
        }

        this.updateVisibleModels();
    }

    /**
     * Updates visible models
     */
    protected updateVisibleModels() {
        const limit = this.getLimit();
        this.models = take(this._models, limit);
        this.modelsWithOverflow = take(this._models, limit + this.infScrollStep());
        this.visibleQty = this.models.length;
        this.events.broadcast(LISTING_UPDATE_VISIBLE_MODELS, this);
    }

    /**
     * Models setter
     */
    setSourceModels(models: number[] | T[]): this {
        // if first item is number then its number array
        if (typeof models[0] === 'number') {
            this.modelsBare = <T[]>this.app.resources[this.type].toModels(models, true);
        } else {
            this.modelsBare = <T[]>models;
        }
        return this;
    }

    /**
     * Bare models getter
     */
    getSourceModels(): T[] {
        return this.modelsBare;
    }

    getFilteredModels(): T[] {
        return this.modelsFiltered;
    }

    /**
     * Adds filter
     */
    addFilter(filter: Filter<T>) {
        this.filtersMap[filter.id] = filter;

        this.subscriptions.add(
            filter.subscribe(() => {
                if (this.config.storage) {
                    this.addFiltersToStorage();
                }

                this.apply();
            })
        );

        if (this.config.storage) {
            const filtersState = this.filtersState;

            if (filtersState?.[filter.id]) {
                filter.setState(filtersState[filter.id]);
            }
        }

        return filter;
    }

    setFiltersState(): void {
        const filtersState = this.filtersState;

        this.filters.forEach(filter => {
            filter.setState(filtersState?.[filter.id] || []);
        });
    }

    getFiltersByAttributes(codes: string[]): Filter<T>[] {
        return this.filters.filter(filter => codes.includes(<string>filter.id));
    }

    /**
     * `getFilteredModelsExceptFilter` is needed to calculate filter qties
     *
     * In typical mode with `Filter` type argument it applies all listing filters except `skipFilter`.
     * This way `skipFilter` is able check qties for each of its
     * options separately.
     *
     * This method is passed to filter models see `OptionAttributeFilter` contstructor
     *
     * @param {Filter} skipFilter Filter to skip (usually filter that calls this method).
     */
    getFilteredModelsExceptFilter(overrides?: IOverride<T>[]): T[] {
        // apply all filters except requrested one
        let models = this.modelsBare;

        const selection: TAttrOptionSelection = {};
        this.filters.forEach(filter => {
            let selectionOverride = overrides?.find(i => i.filter === filter)?.selection;
            // if override is false then skip filter completely
            if (selectionOverride !== false) {
                if (selectionOverride && !Array.isArray(selectionOverride)) {
                    selectionOverride = [selectionOverride];
                }
                // apply filter with optionsl selection override
                models = filter.applyFilter(models, selectionOverride);
                if (['attribute', 'attribute-option'].includes(filter.config.type)) {
                    // register filter for post check
                    const filterSelection =
                        selectionOverride ||
                        (filter.selection as number[]) ||
                        ((filter['options'] as Catalog.IAttributeOption[]) || []).map(i => i.value);
                    if (filterSelection && Array.isArray(filterSelection)) {
                        selection[filter.id] = filterSelection;
                    }
                }
            }
        });

        const event: IListingFilterEvent<T> = {
            listing: this,
            models,
            selection,
            overrides
        };
        this.events.broadcast<IListingFilterEvent<T>>(LISTING_SET_FILTERS, event);

        return this.postFilterCheck(event.models, selection, overrides);
    }

    /**
     * Additional filtration to apply post regular filters
     * we use mainlu on product-listing to apply deep stock checking
     */
    protected postFilterCheck(models: T[], selection: TAttrOptionSelection, overrides?: { filter: Filter<T>; selection: any }[]) {
        return models;
    }

    /**
     * Resets all filters
     */
    resetFilters(): Listing<T> {
        this.filters.forEach(filter => filter.reset());
        return this;
    }

    /**
     * Applies all directive filters to collection,
     * and then calls _sortModels function to sort
     * newly created array of models.
     *
     * Dataflow:
     * modelsBare => modelsFiltered
     * Models to filter are taken from `modelsBare`
     * variable, while result is passed to `modelsFiltered`
     *
     * Note that function is debounced.
     */
    applyFilters(): void {
        // var t1 = performance.now();
        const isInitiated = !!this.modelsFiltered;

        // apply filters
        this.setModelsFiltered(this.getFilteredModelsExceptFilter());

        // anytime models change update infinite scroll setup
        if (isInitiated || !this.limit) {
            this.updateLimits();
        }
        // var t2 = performance.now();

        // go to next step
        this.sortModels();
        // var t3 = performance.now();
    }

    protected setModelsFiltered(models: T[] = this._models) {
        this.modelsFiltered = models;
        this.total = models?.length;
        this.events.broadcast(LISTING_UPDATE_TOTALS_EVENT, this);
    }

    /**
     * Sorts models according to selected sort option
     *
     * Dataflow:
     * modelsFiltered => _listing.models
     * Models to sort are taken from `modelsFiltered`
     * variable, while result is passed to `_listing.models`
     */
    sortModels(): void {
        const models = this.modelsFiltered ? [...this.modelsFiltered] : []; // we need new array here

        const cb = (_models = models) => this.sortModelsCb(_models);

        // sort collection
        if (this.sort) {
            this.sort.applySort(models, cb);
        } else {
            cb();
        }
    }

    /**
     * Called when sorting models is ready
     */
    protected sortModelsCb(_models: T[]) {
        // bind results to innerView context
        this.setFinalModels(_models);
        // mark as ready
        this.ready = true;
        // notify
        this.next();
    }

    get filters() {
        return Object.values(this.filtersMap);
    }

    /**
     * If any filter is applied check
     */
    isFiltered(): boolean {
        return !!this.filters.find(filter => filter.hasSelection);
    }

    get filterSelection() {
        return this.filters.reduce((acc, filter) => (Array.isArray(filter.selection) ? acc.concat(filter.selection as any) : acc), []);
    }

    /**
     * Gets only filtres with matches
     */
    getFiltersWithMatches(): Filter<T>[] {
        return this.filters.filter(i => i.hasAnyMatches);
    }
    /**
     * Gets only filtres with matches
     */
    getFiltersWithManyCurrentMatches(): Filter<T>[] {
        return this.filters.filter(i => i.hasManyCurrentMatches() || i.hasSelection);
    }

    /**
     * Gets only filtres with matches
     */
    getFiltersWithSelection(): Filter<T>[] {
        return this.filters.filter(i => i.hasSelection);
    }

    /**
     * Idenfing function for filters repeater
     */
    filterTrackByFn(index: number, filter: Filter<T>) {
        return filter.id;
    }

    /**
     * Idenfing function for modelx repeater
     */
    modelTrackByFn(index: number, model: T) {
        return model.id;
    }

    /////////////////////////////////////////////////////////////////
    ///
    /// Infinite scroll support
    ///
    /////////////////////////////////////////////////////////////////

    updateLimits() {
        const models = this.modelsFiltered;
        if (models && !this.config.noLimitReset) {
            const step = this.infScrollStep();
            if (['browser', 'universal'].includes(this.client.mode) && this.config.infScrollStep) {
                const init = this.config.infScrollInit;
                this.initialLimit =
                    (init && (typeof init === 'number' ? init : init[this.app.client.sizeId])) ||
                    (this.config.initialStepsVisible || 1) * step;
            } else {
                this.initialLimit = Math.min(models.length, 100); // 100 prods to be printed on server side
            }

            // reduce limit if less models visible now
            if (this.limit) {
                this.limit = Math.min(models.length, Math.max(this.initialLimit, this.limit));
            }
        }
    }

    getLimit(): number {
        return this.limit || this.initialLimit;
    }

    setLimit(value: number = Infinity, silent = false): void {
        if (this.limit !== value) {
            this.limit = value;
            this.updateVisibleModels();
            if (this.config.storage) {
                this.limitState = this.limit;
            }
            if (!silent) {
                this.next();
            }
        }
    }

    setSize(value: number = 4) {
        if (this.sizeState !== value) {
            this.sizeState = value;

            // notify
            this.next();
        }
    }

    getTotal(): number {
        return this.total || 0;
    }

    getVisibleQty(): number {
        return this.visibleQty || 0;
    }

    get hasMore(): boolean {
        if (this.modelsFiltered) {
            const finalModels = this.getFilteredModels();
            if (finalModels) {
                return this.getLimit() < finalModels.length;
            }
        }
        return false;
    }

    /**
     * Increases displayed limit and returns true if all items displayed
     */
    increaseLimit(): void {
        if (this.hasMore) {
            this.setLimit(this.getLimit() + this.infScrollStep());
        }
    }

    protected infScrollStep(): number {
        if (isFunction(this.config.infScrollStep)) {
            return this.config.infScrollStep();
        }
        if (Array.isArray(this.config.infScrollStep)) {
            return this.config.infScrollStep[this.client.sizeId];
        }
        return this.config.infScrollStep || 10;
    }
}
